import React, { useState } from 'react';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    IconButton,
    InputLabel,
    ListItemText,
    MenuItem,
    OutlinedInput,
    Paper,
    Select,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import RefreshIcon from '@mui/icons-material/Refresh';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import {
    useTestMetrics,
    useCreateTestMetric,
    useUpdateTestMetric,
    useDeleteTestMetric,
} from '../../hooks/testMetrics';
import { useDisclaimers } from '../../hooks/disclaimers';
import type {
    TestMetric,
    MetricScale,
    MetricLevel,
    CreateTestMetricDto,
    MetricValueType,
} from '../../types/types';
import {
    kMetricValueTypes,
    kTrendStrategyKeys,
    HEX_COLOR_RE,
    findOverlappingLevels,
} from '../../testMetricConstants';
import { kAvailableIconNames, kCardColors } from '../../testConfigConstants';
import { t } from '../../i18n/t';

const emptyLevel = (priority = 1): MetricLevel => ({
    label: '',
    description: '',
    min: 0,
    max: 0,
    unit: '%',
    colorHex: '#EE696F',
    labelColorHex: '#FFFFFF',
    priority,
});

const emptyScale = (index = 0): MetricScale => ({
    name: '',
    description: '',
    unit: '%',
    min: 0,
    max: 100,
    inverted: false,
    index,
    levels: [],
});

const defaultForm = (): CreateTestMetricDto => ({
    type: '',
    title: '',
    shortTitle: '',
    description: '',
    benefits: '',
    unit: '%',
    valueType: 'percent',
    iconSvg: kAvailableIconNames[0] ?? 'heart.svg',
    iconColorHex: '#4B9FFF',
    isEnabled: true,
    isWorkoutMetric: false,
    isInvertedProgress: false,
    validationKey: '',
    decimals: 0,
    nullDisplay: '—',
    trendStrategy: 'default',
    clampToScale: true,
    formatterKey: 'percent',
    tags: [],
    metadata: {},
    disclaimerIds: [],
    scales: [],
});

const metricToForm = (m: TestMetric): CreateTestMetricDto => ({
    type: m.type,
    title: m.title,
    shortTitle: m.shortTitle,
    description: m.description,
    benefits: m.benefits,
    unit: m.unit,
    valueType: m.valueType,
    iconSvg: m.iconSvg,
    iconColorHex: m.iconColorHex,
    isEnabled: m.isEnabled,
    isWorkoutMetric: m.isWorkoutMetric,
    isInvertedProgress: m.isInvertedProgress,
    validationKey: m.validationKey,
    decimals: m.decimals,
    nullDisplay: m.nullDisplay,
    trendStrategy: m.trendStrategy,
    clampToScale: m.clampToScale,
    formatterKey: m.formatterKey,
    tags: [...(m.tags ?? [])],
    metadata: { ...(m.metadata ?? {}) },
    disclaimerIds: [...(m.disclaimerIds ?? [])],
    scales: (m.scales ?? []).map((s) => ({
        ...s,
        levels: (s.levels ?? []).map((l) => ({ ...l })),
    })),
});

const validateForm = (form: CreateTestMetricDto): string | null => {
    if (!form.type.trim()) return t('testMetrics.enterType');
    if (!form.title.trim()) return t('testMetrics.enterTitle');
    if (!form.validationKey.trim()) return t('testMetrics.enterValidationKey');
    if (!HEX_COLOR_RE.test(form.iconColorHex)) return t('testMetrics.invalidHex');

    for (let si = 0; si < (form.scales?.length ?? 0); si++) {
        const scale = form.scales![si];
        if (scale.min >= scale.max) return t('testMetrics.scaleMinMax', { name: scale.name || `#${si + 1}` });
        for (const level of scale.levels) {
            if (level.min > level.max) return t('testMetrics.levelMinMax', { label: level.label || '?' });
            if (!HEX_COLOR_RE.test(level.colorHex) || !HEX_COLOR_RE.test(level.labelColorHex)) {
                return t('testMetrics.invalidHex');
            }
        }
        const overlap = findOverlappingLevels(scale.levels);
        if (overlap) return `${scale.name || `Scale ${si + 1}`}: ${overlap}`;
    }
    return null;
};

const DashboardTestMetrics = () => {
    const { data: metrics = [], isLoading, refetch } = useTestMetrics();
    const deleteMutation = useDeleteTestMetric();
    const [createOpen, setCreateOpen] = useState(false);
    const [editingMetric, setEditingMetric] = useState<TestMetric | null>(null);

    const handleDelete = async (metric: TestMetric) => {
        if (!window.confirm(t('testMetrics.deleteConfirm', { type: metric.type }))) return;
        try {
            await deleteMutation.mutateAsync(metric.id);
        } catch (e) {
            console.error(e);
            alert(t('testMetrics.deleteFailed'));
        }
    };

    if (isLoading) {
        return (
            <Box display="flex" justifyContent="center" p={4}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            <Typography variant="h6" sx={{ mb: 2 }}>
                {t('testMetrics.title')}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                <Button variant="contained" onClick={() => setCreateOpen(true)}>
                    {t('testMetrics.new')}
                </Button>
                <Tooltip title={t('testMetrics.refresh')}>
                    <IconButton onClick={() => refetch()}>
                        <RefreshIcon />
                    </IconButton>
                </Tooltip>
            </Box>

            <TableContainer component={Paper}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>{t('testMetrics.colType')}</TableCell>
                            <TableCell>{t('testMetrics.colTitle')}</TableCell>
                            <TableCell>{t('testMetrics.colValueType')}</TableCell>
                            <TableCell>{t('testMetrics.colEnabled')}</TableCell>
                            <TableCell>{t('testMetrics.colScales')}</TableCell>
                            <TableCell>{t('testMetrics.colDisclaimers')}</TableCell>
                            <TableCell align="right">{t('testMetrics.colActions')}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {metrics.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={7} align="center">
                                    {t('testMetrics.empty')}
                                </TableCell>
                            </TableRow>
                        ) : (
                            metrics.map((metric) => (
                                <TableRow key={metric.id}>
                                    <TableCell>{metric.type}</TableCell>
                                    <TableCell>{metric.title}</TableCell>
                                    <TableCell>{metric.valueType}</TableCell>
                                    <TableCell>
                                        <Chip
                                            size="small"
                                            label={metric.isEnabled ? t('testMetrics.enabled') : t('testMetrics.disabled')}
                                            color={metric.isEnabled ? 'success' : 'default'}
                                        />
                                    </TableCell>
                                    <TableCell>{metric.scales?.length ?? 0}</TableCell>
                                    <TableCell>{metric.disclaimerIds?.length ?? 0}</TableCell>
                                    <TableCell align="right">
                                        <Tooltip title={t('common.edit')}>
                                            <IconButton size="small" onClick={() => setEditingMetric(metric)}>
                                                <EditIcon />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title={t('common.delete')}>
                                            <IconButton size="small" color="error" onClick={() => handleDelete(metric)}>
                                                <DeleteIcon />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {createOpen && (
                <TestMetricFormDialog title={t('testMetrics.dialogNew')} onClose={() => setCreateOpen(false)} />
            )}
            {editingMetric && (
                <TestMetricFormDialog
                    title={t('testMetrics.dialogEdit')}
                    metric={editingMetric}
                    onClose={() => setEditingMetric(null)}
                />
            )}
        </Box>
    );
};

interface TestMetricFormDialogProps {
    title: string;
    metric?: TestMetric;
    onClose: () => void;
}

const TestMetricFormDialog: React.FC<TestMetricFormDialogProps> = ({ title, metric, onClose }) => {
    const createMutation = useCreateTestMetric();
    const updateMutation = useUpdateTestMetric();
    const { data: disclaimers = [] } = useDisclaimers();
    const isEdit = metric != null;
    const [form, setForm] = useState<CreateTestMetricDto>(() => (metric ? metricToForm(metric) : defaultForm()));
    const [tagsInput, setTagsInput] = useState(() => (metric?.tags ?? []).join(', '));
    const [saving, setSaving] = useState(false);

    const setField = <K extends keyof CreateTestMetricDto>(key: K, value: CreateTestMetricDto[K]) => {
        setForm((prev) => ({ ...prev, [key]: value }));
    };

    const updateScale = (scaleIndex: number, patch: Partial<MetricScale>) => {
        setForm((prev) => {
            const scales = [...(prev.scales ?? [])];
            scales[scaleIndex] = { ...scales[scaleIndex], ...patch };
            return { ...prev, scales };
        });
    };

    const addScale = () => {
        setForm((prev) => ({
            ...prev,
            scales: [...(prev.scales ?? []), emptyScale(prev.scales?.length ?? 0)],
        }));
    };

    const removeScale = (scaleIndex: number) => {
        setForm((prev) => {
            const scales = (prev.scales ?? []).filter((_, i) => i !== scaleIndex);
            return {
                ...prev,
                scales: scales.map((s, i) => ({ ...s, index: i })),
            };
        });
    };

    const addLevel = (scaleIndex: number) => {
        const scale = form.scales?.[scaleIndex];
        const nextPriority = (scale?.levels?.length ?? 0) + 1;
        updateScale(scaleIndex, {
            levels: [...(scale?.levels ?? []), emptyLevel(nextPriority)],
        });
    };

    const updateLevel = (scaleIndex: number, levelIndex: number, patch: Partial<MetricLevel>) => {
        setForm((prev) => {
            const scales = [...(prev.scales ?? [])];
            const levels = [...(scales[scaleIndex].levels ?? [])];
            levels[levelIndex] = { ...levels[levelIndex], ...patch };
            scales[scaleIndex] = { ...scales[scaleIndex], levels };
            return { ...prev, scales };
        });
    };

    const removeLevel = (scaleIndex: number, levelIndex: number) => {
        setForm((prev) => {
            const scales = [...(prev.scales ?? [])];
            const levels = (scales[scaleIndex].levels ?? []).filter((_, i) => i !== levelIndex);
            scales[scaleIndex] = { ...scales[scaleIndex], levels };
            return { ...prev, scales };
        });
    };

    const handleSave = async () => {
        const normalizedScales = (form.scales ?? []).map((scale) => ({
            ...scale,
            levels: (scale.levels ?? []).map((level) => ({
                ...level,
                description: level.description?.trim() || level.label.trim() || 'Level',
                unit: level.unit?.trim() || scale.unit,
                labelColorHex: level.labelColorHex || '#FFFFFF',
            })),
        }));
        const dto: CreateTestMetricDto = {
            ...form,
            scales: normalizedScales,
            tags: tagsInput.split(',').map((t) => t.trim()).filter(Boolean),
        };
        const err = validateForm(dto);
        if (err) {
            alert(err);
            return;
        }

        setSaving(true);
        try {
            if (isEdit && metric) {
                await updateMutation.mutateAsync({ id: metric.id, dto });
            } else {
                await createMutation.mutateAsync(dto);
            }
            onClose();
        } catch (e) {
            console.error(e);
            alert(t('testMetrics.saveFailed'));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open fullWidth maxWidth="lg" onClose={onClose} scroll="paper">
            <DialogTitle>{title}</DialogTitle>
            <DialogContent dividers>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Typography variant="subtitle2">{t('testMetrics.sectionMain')}</Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                        <TextField
                            label={t('testMetrics.fieldType')}
                            value={form.type}
                            onChange={(e) => setField('type', e.target.value)}
                            size="small"
                            disabled={isEdit}
                        />
                        <TextField
                            label={t('testMetrics.fieldValidationKey')}
                            value={form.validationKey}
                            onChange={(e) => setField('validationKey', e.target.value)}
                            size="small"
                        />
                        <TextField
                            label={t('testMetrics.fieldTitle')}
                            value={form.title}
                            onChange={(e) => setField('title', e.target.value)}
                            size="small"
                        />
                        <TextField
                            label={t('testMetrics.fieldShortTitle')}
                            value={form.shortTitle}
                            onChange={(e) => setField('shortTitle', e.target.value)}
                            size="small"
                        />
                        <TextField
                            select
                            label={t('testMetrics.fieldValueType')}
                            value={form.valueType}
                            onChange={(e) => setField('valueType', e.target.value as MetricValueType)}
                            size="small"
                        >
                            {kMetricValueTypes.map((v) => (
                                <MenuItem key={v} value={v}>{v}</MenuItem>
                            ))}
                        </TextField>
                        <TextField
                            label={t('testMetrics.fieldUnit')}
                            value={form.unit}
                            onChange={(e) => setField('unit', e.target.value)}
                            size="small"
                        />
                        <TextField
                            select
                            label={t('testMetrics.fieldIcon')}
                            value={form.iconSvg}
                            onChange={(e) => setField('iconSvg', e.target.value)}
                            size="small"
                        >
                            {kAvailableIconNames.map((n) => (
                                <MenuItem key={n} value={n}>{n}</MenuItem>
                            ))}
                        </TextField>
                        <TextField
                            select
                            label={t('testMetrics.fieldIconColor')}
                            value={form.iconColorHex}
                            onChange={(e) => setField('iconColorHex', e.target.value)}
                            size="small"
                        >
                            {kCardColors.map((c) => (
                                <MenuItem key={c.hex} value={c.hex}>{c.name}</MenuItem>
                            ))}
                        </TextField>
                        <TextField
                            select
                            label={t('testMetrics.fieldTrendStrategy')}
                            value={form.trendStrategy}
                            onChange={(e) => setField('trendStrategy', e.target.value)}
                            size="small"
                        >
                            {kTrendStrategyKeys.map((k) => (
                                <MenuItem key={k} value={k}>{k}</MenuItem>
                            ))}
                        </TextField>
                        <TextField
                            label={t('testMetrics.fieldFormatterKey')}
                            value={form.formatterKey}
                            onChange={(e) => setField('formatterKey', e.target.value)}
                            size="small"
                        />
                        <TextField
                            label={t('testMetrics.fieldDecimals')}
                            type="number"
                            value={form.decimals}
                            onChange={(e) => setField('decimals', Number(e.target.value))}
                            size="small"
                            inputProps={{ min: 0 }}
                        />
                        <TextField
                            label={t('testMetrics.fieldNullDisplay')}
                            value={form.nullDisplay}
                            onChange={(e) => setField('nullDisplay', e.target.value)}
                            size="small"
                        />
                        <TextField
                            label={t('testMetrics.fieldTags')}
                            value={tagsInput}
                            onChange={(e) => setTagsInput(e.target.value)}
                            size="small"
                            placeholder="recovery, workout"
                            sx={{ gridColumn: '1 / -1' }}
                        />
                    </Box>
                    <TextField
                        label={t('testMetrics.fieldDescription')}
                        value={form.description}
                        onChange={(e) => setField('description', e.target.value)}
                        multiline
                        minRows={2}
                        size="small"
                        fullWidth
                    />
                    <TextField
                        label={t('testMetrics.fieldBenefits')}
                        value={form.benefits}
                        onChange={(e) => setField('benefits', e.target.value)}
                        multiline
                        minRows={2}
                        size="small"
                        fullWidth
                    />
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                        <FormControlLabel
                            control={<Switch checked={!!form.isEnabled} onChange={(e) => setField('isEnabled', e.target.checked)} />}
                            label={t('testMetrics.fieldEnabled')}
                        />
                        <FormControlLabel
                            control={<Switch checked={!!form.isWorkoutMetric} onChange={(e) => setField('isWorkoutMetric', e.target.checked)} />}
                            label={t('testMetrics.fieldWorkout')}
                        />
                        <FormControlLabel
                            control={<Switch checked={!!form.isInvertedProgress} onChange={(e) => setField('isInvertedProgress', e.target.checked)} />}
                            label={t('testMetrics.fieldInvertedProgress')}
                        />
                        <FormControlLabel
                            control={<Switch checked={!!form.clampToScale} onChange={(e) => setField('clampToScale', e.target.checked)} />}
                            label={t('testMetrics.fieldClamp')}
                        />
                    </Box>

                    <FormControl size="small" fullWidth>
                        <InputLabel>{t('testMetrics.disclaimerIds')}</InputLabel>
                        <Select
                            multiple
                            label={t('testMetrics.disclaimerIds')}
                            value={form.disclaimerIds ?? []}
                            onChange={(e) =>
                                setField(
                                    'disclaimerIds',
                                    typeof e.target.value === 'string'
                                        ? []
                                        : (e.target.value as number[]),
                                )
                            }
                            input={<OutlinedInput label={t('testMetrics.disclaimerIds')} />}
                            renderValue={(selected) =>
                                selected
                                    .map((id) => disclaimers.find((item) => item.id === id)?.code ?? id)
                                    .join(', ')
                            }
                        >
                            {disclaimers.map((disclaimer) => (
                                <MenuItem key={disclaimer.id} value={disclaimer.id}>
                                    <Checkbox checked={(form.disclaimerIds ?? []).includes(disclaimer.id)} />
                                    <ListItemText
                                        primary={disclaimer.code}
                                        secondary={(disclaimer.body ?? '').replace(/\s+/g, ' ').trim().slice(0, 80)}
                                    />
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1 }}>
                        <Typography variant="subtitle2">{t('testMetrics.sectionScales')}</Typography>
                        <Button size="small" startIcon={<AddIcon />} onClick={addScale}>
                            {t('testMetrics.addScale')}
                        </Button>
                    </Box>

                    {(form.scales ?? []).map((scale, si) => (
                        <Accordion key={si} defaultExpanded={si === 0}>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Typography>
                                    {scale.name || t('testMetrics.scaleUntitled', { n: si + 1 })}
                                    {' '}({scale.levels?.length ?? 0} {t('testMetrics.levelsCount')})
                                </Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                                        <TextField
                                            label={t('testMetrics.scaleName')}
                                            value={scale.name}
                                            onChange={(e) => updateScale(si, { name: e.target.value })}
                                            size="small"
                                        />
                                        <TextField
                                            label={t('testMetrics.scaleUnit')}
                                            value={scale.unit}
                                            onChange={(e) => updateScale(si, { unit: e.target.value })}
                                            size="small"
                                        />
                                        <TextField
                                            label={t('testMetrics.scaleMin')}
                                            type="number"
                                            value={scale.min}
                                            onChange={(e) => updateScale(si, { min: Number(e.target.value) })}
                                            size="small"
                                        />
                                        <TextField
                                            label={t('testMetrics.scaleMax')}
                                            type="number"
                                            value={scale.max}
                                            onChange={(e) => updateScale(si, { max: Number(e.target.value) })}
                                            size="small"
                                        />
                                        <TextField
                                            label={t('testMetrics.scaleIndex')}
                                            type="number"
                                            value={scale.index}
                                            onChange={(e) => updateScale(si, { index: Number(e.target.value) })}
                                            size="small"
                                        />
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={scale.inverted}
                                                    onChange={(e) => updateScale(si, { inverted: e.target.checked })}
                                                />
                                            }
                                            label={t('testMetrics.scaleInverted')}
                                        />
                                    </Box>
                                    <TextField
                                        label={t('testMetrics.scaleDescription')}
                                        value={scale.description}
                                        onChange={(e) => updateScale(si, { description: e.target.value })}
                                        multiline
                                        minRows={2}
                                        size="small"
                                        fullWidth
                                    />
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Typography variant="body2">{t('testMetrics.sectionLevels')}</Typography>
                                        <Box sx={{ display: 'flex', gap: 1 }}>
                                            <Button size="small" onClick={() => addLevel(si)}>
                                                {t('testMetrics.addLevel')}
                                            </Button>
                                            <Button size="small" color="error" onClick={() => removeScale(si)}>
                                                {t('testMetrics.removeScale')}
                                            </Button>
                                        </Box>
                                    </Box>
                                    <TableContainer>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>{t('testMetrics.levelLabel')}</TableCell>
                                                    <TableCell>min</TableCell>
                                                    <TableCell>max</TableCell>
                                                    <TableCell>{t('testMetrics.levelPriority')}</TableCell>
                                                    <TableCell>{t('testMetrics.levelColor')}</TableCell>
                                                    <TableCell />
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {(scale.levels ?? []).map((level, li) => (
                                                    <TableRow key={li}>
                                                        <TableCell>
                                                            <TextField
                                                                size="small"
                                                                value={level.label}
                                                                onChange={(e) => updateLevel(si, li, { label: e.target.value })}
                                                            />
                                                        </TableCell>
                                                        <TableCell>
                                                            <TextField
                                                                size="small"
                                                                type="number"
                                                                value={level.min}
                                                                onChange={(e) => updateLevel(si, li, { min: Number(e.target.value) })}
                                                                sx={{ width: 72 }}
                                                            />
                                                        </TableCell>
                                                        <TableCell>
                                                            <TextField
                                                                size="small"
                                                                type="number"
                                                                value={level.max}
                                                                onChange={(e) => updateLevel(si, li, { max: Number(e.target.value) })}
                                                                sx={{ width: 72 }}
                                                            />
                                                        </TableCell>
                                                        <TableCell>
                                                            <TextField
                                                                size="small"
                                                                type="number"
                                                                value={level.priority}
                                                                onChange={(e) => updateLevel(si, li, { priority: Number(e.target.value) })}
                                                                sx={{ width: 64 }}
                                                            />
                                                        </TableCell>
                                                        <TableCell>
                                                            <TextField
                                                                select
                                                                size="small"
                                                                value={level.colorHex}
                                                                onChange={(e) => updateLevel(si, li, { colorHex: e.target.value })}
                                                                sx={{ minWidth: 100 }}
                                                            >
                                                                {kCardColors.map((c) => (
                                                                    <MenuItem key={c.hex} value={c.hex}>{c.name}</MenuItem>
                                                                ))}
                                                            </TextField>
                                                        </TableCell>
                                                        <TableCell>
                                                            <IconButton size="small" color="error" onClick={() => removeLevel(si, li)}>
                                                                <DeleteIcon fontSize="small" />
                                                            </IconButton>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Box>
                            </AccordionDetails>
                        </Accordion>
                    ))}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>{t('common.cancel')}</Button>
                <Button variant="contained" onClick={handleSave} disabled={saving}>
                    {t('common.save')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default DashboardTestMetrics;
